import { useEffect, useState } from "react";
import { formatFileSize } from "../../utils/imageSizeUtils";
import moment from "moment";
import {
  downloadFile,
  fetchFiles,
  removeFile,
  uploadFile,
} from "../../api/fetchFiles";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import { Button, Dialog, FilePicker } from "@rio-cloud/rio-uikit";
import ErrorDialog from "../dialogs/ErrorDialog";

interface IUploadProps {
  id: string;
  path: string;
  disabled: boolean;
  pendingFiles?: File[];
  onPickPending?: (files: File[]) => void;
  onRemovePending?: (fileName: string) => void;
}

interface IUploadFile {
  name: string;
  updatedAt: string;
  contentType: string;
  contentSize: number;
  link: string;
}

const toUploadFile = (file: File): IUploadFile => ({
  name: file.name,
  updatedAt: new Date(file.lastModified).toLocaleDateString("pt-BR"),
  contentType: file.type,
  contentSize: file.size,
  link: URL.createObjectURL(file),
});

const getFiles = async (path: string) => {
  const response = await fetchFiles(path);
  return response;
};

const Upload = (props: IUploadProps) => {
  const isPending = props.pendingFiles !== undefined;
  const [files, setFiles] = useState<IUploadFile[]>([]);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (isPending) return;
    const fetchData = async () => {
      const files = await getFiles(props.path);
      setFiles(files);
    };
    fetchData();
  }, []);

  const displayFiles = isPending
    ? (props.pendingFiles ?? []).map(toUploadFile)
    : files;

  return (
    <div id={props.id}>
      {!props.disabled && (
        <FilePicker
          displayMode="dropzone"
          className="margin-bottom-20"
          onPick={async (acceptedFiles: FileList | null) => {
            const maxSize = 20 * 1024 * 1024;
            const filePicked = acceptedFiles?.[0];

            if (!filePicked) return;

            if (filePicked.size > maxSize) {
              return setUploadErrorMessage(
                "O arquivo selecionado excede o limite de 5MB. Escolha um arquivo menor."
              );
            }

            if (isPending) {
              props.onPickPending?.(Array.from(acceptedFiles!));
            } else {
              const uploaded = await uploadFile({
                path: props.path,
                file: Array.from(acceptedFiles!),
              });
              if (!uploaded)
                return setUploadErrorMessage(
                  "Arquivo invalido. Erro ao realizar o upload, verifique o arquivo e tente novamente."
                );
              setFiles(prev => [
                ...prev,
                {
                  name: filePicked.name,
                  updatedAt: new Date(filePicked.lastModified).toLocaleDateString("pt-BR"),
                  contentType: filePicked.type,
                  contentSize: filePicked.size,
                  link: URL.createObjectURL(filePicked),
                },
              ]);
            }
          }}
          multiple={true}
        >
          {dropArea}
        </FilePicker>
      )}

      <div className="display-grid grid-cols-1 grid-cols-3-sm gap-25">
        {displayFiles.length > 0 ? (
          displayFiles.map((file, index) => (
            <FilePreview
              key={index}
              file={file}
              path={props.path}
              remove={() => {
                if (isPending) {
                  props.onRemovePending?.(file.name);
                } else {
                  setFiles(prev => prev.filter(f => f.name !== file.name));
                }
              }}
              skipApiDelete={isPending}
              disabled={props.disabled}
            />
          ))
        ) : (
          <div className="text-color-dark text-italic">Nenhum arquivo</div>
        )}
      </div>

      <ErrorDialog
        show={!!uploadErrorMessage}
        title={"Erro ao fazer upload"}
        body={uploadErrorMessage ?? ""}
        onClose={() => setUploadErrorMessage(undefined)}
      />
    </div>
  );
};

const dropArea = ({ isDragActive }: { isDragActive: boolean }) => (
  <div
    className={`
              rounded border border-style-dashed border-color-gray bg-lightest text-size-14
              text-center text-color-darker opacity-50 padding-25 cursor-pointer
          `}
  >
    <span className="rioglyph rioglyph-files text-size-200pct opacity-50 margin-top-20" />
    <div className="margin-top-10">
      {isDragActive
        ? "Jogue os arquivos aqui ..."
        : "Arraste seus arquivos para cá ou clique para selecionar no computador"}
    </div>
  </div>
);

const FilePreview = ({
  file,
  path,
  remove,
  disabled,
  skipApiDelete,
}: {
  file: IUploadFile | undefined;
  path: string;
  remove: () => void;
  disabled: boolean;
  skipApiDelete?: boolean;
}) => {
  const [deleteFileDialog, setDeleteFileDialog] = useState<boolean>(false);
  const [previewDialog, setPreviewDialog] = useState<boolean>(false);

  const getFileThumbnail = (file: IUploadFile): string => {
    const type = file.contentType.toLowerCase();

    if (type.startsWith("image/")) {
      return file.link;
    } else if (type === "application/pdf") {
      return "/assets/images/pdf-icon.svg";
    } else if (type.startsWith("video/")) {
      return "/assets/images/video-icon.svg";
    } else if (
      type === "application/zip" ||
      type === "application/x-rar-compressed"
    ) {
      return "/assets/images/zip-icon.svg";
    } else {
      return "/assets/images/file-icon.svg";
    }
  };
  return (
    <>
      <div
        className="panel panel-default margin-top-20 cursor-pointer"
        onClick={async () => {
          if (file) setPreviewDialog(true);
        }}
      >
        <div className="panel-body">
          {file && (
            <div className="flex">
              {file.link && (
                <div className="thumbnail margin-right-10 flex justify-center items-center w-[200px] h-[120px]">
                  <img
                    src={getFileThumbnail(file)}
                    alt={file.name}
                    className={
                      file.contentType.toLowerCase().startsWith("image/")
                        ? ""
                        : "dark:invert"
                    }
                    style={{
                      width: "200px",
                      height:
                        file.contentType.includes("png") ||
                        file.contentType.includes("jpeg") ||
                        file.contentType.includes("jpg")
                          ? "100px"
                          : "65px",
                      objectFit:
                        file.contentType.includes("png") ||
                        file.contentType.includes("jpeg") ||
                        file.contentType.includes("jpg")
                          ? "cover"
                          : "contain",
                    }}
                  />
                </div>
              )}
              <div className="flex justify-between w-full">
                <div>
                  <div>
                    <b>{`${file?.name}`}</b>
                  </div>
                  <div className="w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">
                    tipo: {`${file?.contentType}`}
                  </div>
                  <div>tamanho: {formatFileSize(file?.contentSize)}</div>
                  <div>
                    data da última modificação:{" "}
                    {file?.updatedAt
                      ? moment(file.updatedAt).format("DD/MM/YYYY")
                      : "Data não disponível"}
                  </div>{" "}
                  <div>caminho: {`${file?.name}`}</div>
                </div>
                {!disabled && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteFileDialog(true);
                    }}
                  >
                    <span className="rioglyph rioglyph-remove" />
                  </div>
                )}
              </div>
            </div>
          )}
          {!file && (
            <div className="text-color-dark text-italic">Nenhum arquivo</div>
          )}
        </div>
      </div>

      <ConfirmDialog
        show={deleteFileDialog}
        title="Remover arquivo"
        body="Deseja realmente remover o arquivo?"
        onClose={() => setDeleteFileDialog(false)}
        onConfirm={async () => {
          if (!file) return;
          if (skipApiDelete) {
            remove();
            setDeleteFileDialog(false);
          } else {
            const response = await removeFile(path, file.name);
            if (response) {
              remove();
              setDeleteFileDialog(false);
            }
          }
        }}
      />

      <Dialog
        show={previewDialog}
        title={file?.name}
        onEsc={() => setPreviewDialog(false)}
        onClose={() => setPreviewDialog(false)}
        bsSize="lg"
        body={
          <div className="flex justify-center">
            {file?.contentType.startsWith("image/") ? (
              <img
                src={file?.link}
                alt={file?.name}
                className="max-w-full max-h-[600px]"
              />
            ) : (
              <div className="text-color-dark">
                Pré-visualização indisponível para este tipo de arquivo.
              </div>
            )}
          </div>
        }
        footer={
          <>
            <div className="display-flex justify-content-end">
              {file?.link && (
                <a
                  href={file.link}
                  style={{ textDecoration: "none" }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button bsStyle="primary" className="margin-right-10">
                    Download arquivo
                  </Button>
                </a>
              )}
              <Button
                bsStyle="default"
                onClick={() => setPreviewDialog(false)}
              >
                Fechar
              </Button>
            </div>
          </>
        }
      />
    </>
  );
};

export default Upload;
